import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useLocation } from 'react-router-dom'
import { apiFetch } from '../config/api'
import { ALL_CATALOG_CATEGORY_KEYS, CatalogCategoryKey } from '../catalog/categoryKeys'

type CatalogMap = Record<string, string[]>

const SCOPED_CATALOG_KEYS = new Set<string>(['base_heat_number', 'floor_weld_id', 'project_client_spec'])

/** Project id from `/projects/:id/next/...` or `/projects/:id/edit` */
function catalogProjectIdFromPathname(pathname: string): string | undefined {
  const m = pathname.match(/^\/projects\/([^/]+)\/(?:next|edit)(?:\/|$)/)
  return m ? m[1] : undefined
}

interface CatalogContextValue {
  labelsByCategory: CatalogMap
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  /** Current project id when route is under `/projects/:id/...`; drives scoped catalog fetch. */
  catalogProjectId: string | undefined
  /** Sorted unique labels for one category (from API only). */
  getLabels: (key: CatalogCategoryKey) => string[]
  /** Merge API labels with extra values (e.g. from saved JSON) for display. */
  getLabelsMerged: (key: CatalogCategoryKey, extras?: string[]) => string[]
  addLabel: (key: CatalogCategoryKey, label: string, projectId?: string) => Promise<void>
}

const CatalogContext = createContext<CatalogContextValue | undefined>(undefined)

function mergeUnique(base: string[], extras: string[] | undefined): string[] {
  const set = new Set<string>()
  base.forEach((b) => set.add(b))
  ;(extras || []).forEach((e) => {
    const t = e.trim()
    if (t) set.add(t)
  })
  return Array.from(set).sort((a, b) => a.localeCompare(b))
}

export function CatalogProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const catalogProjectId = useMemo(() => catalogProjectIdFromPathname(pathname), [pathname])

  const [labelsByCategory, setLabelsByCategory] = useState<CatalogMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const otherKeys = ALL_CATALOG_CATEGORY_KEYS.filter((k) => !SCOPED_CATALOG_KEYS.has(k))
      const qsOther = encodeURIComponent(otherKeys.join(','))
      const dataOther = await apiFetch<Record<string, Array<{ id: number; label: string; sortOrder: number }>>>(
        `/api/catalog?categories=${qsOther}`
      )

      const scopedKeys = ALL_CATALOG_CATEGORY_KEYS.filter((k) => SCOPED_CATALOG_KEYS.has(k))
      const qsScoped = encodeURIComponent(scopedKeys.join(','))
      let dataScoped: Record<string, Array<{ id: number; label: string; sortOrder: number }>> = {}
      if (catalogProjectId) {
        dataScoped = await apiFetch(
          `/api/catalog?categories=${qsScoped}&projectId=${encodeURIComponent(
            catalogProjectId
          )}&strictProjectOnly=1`
        )
      } else {
        dataScoped = await apiFetch(`/api/catalog?categories=${qsScoped}`)
      }

      const mergedFetch = { ...dataOther, ...dataScoped }
      const next: CatalogMap = {}
      for (const key of ALL_CATALOG_CATEGORY_KEYS) {
        const rows = mergedFetch[key] || []
        next[key] = rows.map((r) => r.label)
      }
      setLabelsByCategory(next)
    } catch (err: any) {
      console.error('Catalog load failed:', err)
      setError(err.message || 'Failed to load catalog')
      setLabelsByCategory({})
    } finally {
      setLoading(false)
    }
  }, [catalogProjectId])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  const getLabels = useCallback(
    (key: CatalogCategoryKey) => labelsByCategory[key] || [],
    [labelsByCategory]
  )

  const getLabelsMerged = useCallback(
    (key: CatalogCategoryKey, extras?: string[]) => mergeUnique(getLabels(key), extras),
    [getLabels]
  )

  const addLabel = useCallback(
    async (key: CatalogCategoryKey, label: string, projectId?: string) => {
      const trimmed = label.trim()
      if (!trimmed) return
      const pid = SCOPED_CATALOG_KEYS.has(key) ? projectId?.trim() || catalogProjectId : undefined
      if (SCOPED_CATALOG_KEYS.has(key) && !pid) {
        throw new Error('projectId is required for this catalog category')
      }
      await apiFetch(`/api/catalog`, {
        method: 'POST',
        body: JSON.stringify({
          categoryKey: key,
          label: trimmed,
          ...(pid ? { projectId: pid } : {})
        })
      })
      await fetchAll()
    },
    [fetchAll, catalogProjectId]
  )

  const value = useMemo(
    () => ({
      labelsByCategory,
      loading,
      error,
      refresh: fetchAll,
      catalogProjectId,
      getLabels,
      getLabelsMerged,
      addLabel
    }),
    [labelsByCategory, loading, error, fetchAll, catalogProjectId, getLabels, getLabelsMerged, addLabel]
  )

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
}

export function useCatalog() {
  const ctx = useContext(CatalogContext)
  if (!ctx) throw new Error('useCatalog must be used within CatalogProvider')
  return ctx
}
